import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import { fetchGradingPeriods, periodsForOffering } from "../../utils/gradingPeriods";
import s from "./MyGradingPage.module.css";

const DRAFT_PREFIX = "gradebook.draft.";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

/** Stable hue from any seed — same idiom used in the encoder + student gradebook. */
function hueFor(seed) {
  const str = String(seed || "•");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}
function abbrev(src) {
  const v = (src || "").toString();
  const words = v.replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return v.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "S";
}

/** Count cells with an unsaved-draft entry in localStorage for the given
 *  offering id. Mirrors the encoder's draft schema (schema:1, grades) so
 *  the hub can tell a teacher "you have 3 unsaved cells" without opening
 *  the encoder. Pure / synchronous — runs on every render but is O(1)
 *  per card with bounded JSON size, so it stays cheap. */
function draftDirtyCountFor(offeringId, baselineGrades) {
  try {
    const raw = window.localStorage.getItem(DRAFT_PREFIX + offeringId);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.grades) return 0;
    // Diff the draft against the loaded baseline (passed in as the same
    // shape the encoder builds — { [studentId]: { [periodCode]: cell } }).
    let n = 0;
    const ids = new Set([
      ...Object.keys(parsed.grades || {}),
      ...Object.keys(baselineGrades || {}),
    ]);
    for (const id of ids) {
      const codes = new Set([
        ...Object.keys(parsed.grades?.[id] || {}),
        ...Object.keys(baselineGrades?.[id] || {}),
      ]);
      for (const p of codes) {
        const a = parsed.grades?.[id]?.[p] || { score: "", remarks: "" };
        const b = baselineGrades?.[id]?.[p] || { score: "", remarks: "" };
        if ((a.score || "") !== (b.score || "") ||
            (a.remarks || "") !== (b.remarks || "")) n++;
      }
    }
    return n;
  } catch { return 0; }
}

/** Workspace hook — fetches everything the gradebook hub needs in one go.
 *  Separate from useTeacherData (TeacherDashboard's) so this page can
 *  evolve independently without touching the dashboard contract. Each
 *  endpoint is server-side scoped to the teacher's staff id, so no
 *  whole-school dataset enters the browser. */
function useTeacherWorkspace(teacherStaffId) {
  const [state, setState] = useState({
    loading: true,
    error: "",
    offerings:   [],
    sections:    {},
    subjects:    {},
    semesters:   {},
    schoolYears: {},
    students:    [],
    enrollments: [],
    gradeLevels: {},
    periodRows:  [],
    activeSy: null,
    activeSem: null,
  });

  useEffect(() => {
    if (!teacherStaffId) {
      setState(p => ({ ...p, loading: false }));
      return undefined;
    }
    let cancelled = false;
    setState(p => ({ ...p, loading: true, error: "" }));
    const safe = (promise, fallback) => promise.then(r => r.ok ? r.json() : fallback).catch(() => fallback);
    Promise.all([
      safe(fetch(`${API_BASE}/api/class-offerings?teacherStaffId=${teacherStaffId}`), []),
      safe(fetch(`${API_BASE}/api/subjects`), []),
      safe(fetch(`${API_BASE}/api/sections`), []),
      safe(fetch(`${API_BASE}/api/semesters`), []),
      safe(fetch(`${API_BASE}/api/school-years`), []),
      safe(fetch(`${API_BASE}/api/students?teacherStaffId=${teacherStaffId}`), []),
      safe(fetch(`${API_BASE}/api/enrollments?teacherStaffId=${teacherStaffId}`), []),
      safe(fetch(`${API_BASE}/api/grade-levels`), []),
      fetchGradingPeriods(),
    ]).then(([offerings, subjects, sections, semesters, schoolYears, students, enrollments, gradeLevels, periodRows]) => {
      if (cancelled) return;
      const activeSy  = schoolYears.find(y => y.active) || schoolYears[0] || null;
      const activeSem = semesters.find(sm => sm.active)
        || (activeSy ? semesters.find(sm => sm.schoolYearId === activeSy.id) : null)
        || semesters[0] || null;
      setState({
        loading: false,
        error: "",
        offerings,
        sections:    Object.fromEntries(sections.map(r => [r.id, r])),
        subjects:    Object.fromEntries(subjects.map(r => [r.id, r])),
        semesters:   Object.fromEntries(semesters.map(r => [r.id, r])),
        schoolYears: Object.fromEntries(schoolYears.map(r => [r.id, r])),
        students,
        enrollments,
        gradeLevels: Object.fromEntries(gradeLevels.map(r => [r.id, r])),
        periodRows,
        activeSy,
        activeSem,
      });
    }).catch(e => {
      if (!cancelled) setState(p => ({ ...p, loading: false, error: e.message || "Failed to load" }));
    });
    return () => { cancelled = true; };
  }, [teacherStaffId]);

  return state;
}

/** Fetches the saved grades for every offering passed in — one /api/grades
 *  call per offering, all in parallel. Returns a map { offeringId →
 *  { counts: { [periodCode]: n }, baselineGrades } } so the card can
 *  render "14 / 19 · 1st Quarter" + drive the draft-diff math.
 *
 *  Cap at MAX_PARALLEL so an unusually heavy teaching load doesn't fire
 *  hundreds of requests; remaining offerings come back blank and the
 *  card just shows "—" for progress (still safe, just less rich). */
function useOfferingGrades(offerings) {
  const MAX_PARALLEL = 24;
  const [byId, setById] = useState({});
  const [gradesLoading, setGradesLoading] = useState(false);

  // Stable key — only re-fetches when the SET of offering ids changes.
  const idsKey = useMemo(() =>
    (offerings || []).slice(0, MAX_PARALLEL).map(o => o.id).sort().join(","),
    [offerings]
  );

  useEffect(() => {
    if (!offerings || offerings.length === 0) {
      setById({});
      return undefined;
    }
    let cancelled = false;
    setGradesLoading(true);
    const targets = offerings.slice(0, MAX_PARALLEL);
    Promise.all(
      targets.map(o =>
        fetch(`${API_BASE}/api/grades?classOfferingId=${o.id}`)
          .then(r => r.ok ? r.json() : [])
          .catch(() => [])
          .then(rows => ({ id: o.id, rows: Array.isArray(rows) ? rows : [] }))
      )
    ).then(results => {
      if (cancelled) return;
      const out = {};
      for (const { id, rows } of results) {
        const counts = {};
        const grouped = {};
        for (const r of rows) {
          const sid = r.studentId;
          if (!grouped[sid]) grouped[sid] = {};
          grouped[sid][r.period] = {
            score:   r.score == null ? "" : String(r.score),
            remarks: r.remarks || "",
          };
          if (r.score != null) counts[r.period] = (counts[r.period] || 0) + 1;
        }
        out[id] = { counts, baselineGrades: grouped };
      }
      setById(out);
      setGradesLoading(false);
    }).catch(() => {
      if (!cancelled) setGradesLoading(false);
    });
    return () => { cancelled = true; };
  }, [idsKey, offerings]);

  return { byId, gradesLoading };
}

export default function MyGradingPage() {
  const user = getUser();
  const teacherStaffId = user?.linkedEntity?.id;
  const data = useTeacherWorkspace(teacherStaffId);
  const navigate = useNavigate();

  // Filters: active term (default) vs all terms, plus section narrowing.
  const [scope,     setScope]     = useState("active");   // "active" | "all"
  const [sectionId, setSectionId] = useState("");          // "" = all sections
  const [sortBy,    setSortBy]    = useState("needs");     // "needs" | "subject" | "section"

  // Offerings narrowed by the active filters — drives both the stat
  // tiles and the card grid so they stay in lockstep.
  const visibleOfferings = useMemo(() => {
    let rows = data.offerings || [];
    if (scope === "active" && data.activeSem) {
      rows = rows.filter(o => o.semesterId === data.activeSem.id);
    }
    if (sectionId) {
      rows = rows.filter(o => String(o.sectionId) === String(sectionId));
    }
    return rows;
  }, [data.offerings, data.activeSem, scope, sectionId]);

  // Grades summary per offering — kicks off after the offerings list
  // resolves so the hub can show "14 / 19 midterm" progress per card.
  const { byId: gradesByOffering, gradesLoading } = useOfferingGrades(visibleOfferings);

  // Total students per section (the cohort we grade against). Computed
  // from the enrollments payload, scoped to the active semester when
  // scope === "active" so the count matches what each card encodes.
  const sectionRoster = useMemo(() => {
    const map = new Map();
    for (const e of data.enrollments || []) {
      const status = String(e.status || "ENROLLED").toUpperCase();
      if (status !== "ENROLLED") continue;
      if (scope === "active" && data.activeSem &&
          String(e.semesterId) !== String(data.activeSem.id)) continue;
      const key = String(e.sectionId);
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [data.enrollments, scope, data.activeSem]);

  // Card models — one per offering with everything the card / sort needs.
  const cards = useMemo(() => {
    const rows = visibleOfferings.map(o => {
      const subj  = data.subjects[o.subjectId]   || null;
      const sec   = data.sections[o.sectionId]   || null;
      const sem   = data.semesters[o.semesterId] || null;
      const grade = sec ? data.gradeLevels[sec.gradeLevelId] : null;
      const roster = sectionRoster.get(String(o.sectionId)) || 0;
      const g     = gradesByOffering[o.id] || null;
      // This class's grading periods (1st/2nd Quarter, Midterm/Final…).
      const periods = periodsForOffering({
        offering: o,
        sections: Object.values(data.sections),
        gradeLevels: Object.values(data.gradeLevels),
        semesters: Object.values(data.semesters),
        rows: data.periodRows,
      }).map(pp => ({ ...pp, count: g?.counts?.[pp.code] || 0 }));
      const encoded = periods.reduce((n, pp) => n + pp.count, 0);
      const draftCount = draftDirtyCountFor(o.id, g?.baselineGrades || {});

      // "Needs encoding" weight — bigger when more cells are missing
      // relative to the roster. Drives the default sort so the first
      // card the teacher sees is the one most behind.
      const pendingCells = roster > 0 ? Math.max(0, roster * periods.length - encoded) : 0;
      return {
        offering: o,
        subject:  subj,
        section:  sec,
        semester: sem,
        grade,
        roster,
        periods,
        draftCount,
        pendingCells,
      };
    });
    rows.sort((a, b) => {
      if (sortBy === "subject") {
        return (a.subject?.title || "").localeCompare(b.subject?.title || "");
      }
      if (sortBy === "section") {
        return (a.section?.name || "").localeCompare(b.section?.name || "");
      }
      // default: needs encoding first, then with drafts, then alphabetical
      if (b.pendingCells !== a.pendingCells) return b.pendingCells - a.pendingCells;
      if (b.draftCount   !== a.draftCount)   return b.draftCount - a.draftCount;
      return (a.subject?.title || "").localeCompare(b.subject?.title || "");
    });
    return rows;
  }, [visibleOfferings, data.subjects, data.sections, data.semesters,
      data.gradeLevels, data.periodRows, sectionRoster, gradesByOffering, sortBy]);

  // Aggregate stats across visible offerings.
  const stats = useMemo(() => {
    let students = 0, slots = 0, encoded = 0, drafts = 0;
    const sectionSet = new Set();
    const byLabel = new Map(); // period label -> { slots, encoded }
    for (const c of cards) {
      students += c.roster;
      drafts   += c.draftCount;
      if (c.section?.id) sectionSet.add(c.section.id);
      for (const pp of c.periods) {
        slots   += c.roster;
        encoded += pp.count;
        const agg = byLabel.get(pp.label) || { slots: 0, encoded: 0 };
        agg.slots += c.roster; agg.encoded += pp.count;
        byLabel.set(pp.label, agg);
      }
    }
    const pct = (e, s2) => (s2 > 0 ? Math.round((e / s2) * 100) : 0);
    return {
      classes:       cards.length,
      sections:      sectionSet.size,
      uniqueStudents: students, // (counted as roster × classes; rough volume metric)
      combinedPct:   pct(encoded, slots),
      periodHint:    [...byLabel.entries()].map(([label, a]) => `${label} ${pct(a.encoded, a.slots)}%`).join(" · "),
      drafts,
    };
  }, [cards]);

  // Section filter options — only sections the teacher actually teaches
  // (so the dropdown doesn't list every section in the school).
  const sectionOptions = useMemo(() => {
    const seen = new Map();
    for (const o of data.offerings || []) {
      const sec = data.sections[o.sectionId];
      if (sec && !seen.has(sec.id)) {
        const grade = data.gradeLevels[sec.gradeLevelId];
        seen.set(sec.id, {
          id: sec.id,
          label: grade ? `${grade.name} · ${sec.name}` : sec.name,
        });
      }
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [data.offerings, data.sections, data.gradeLevels]);

  const termLabel = (() => {
    if (!data.activeSy || !data.activeSem) return "";
    return `${data.activeSy.label} · ${data.activeSem.label} sem`;
  })();

  // Account-not-linked guard — matches the dashboard's idiom so a teacher
  // sees the same friendly message instead of an empty page.
  if (!teacherStaffId) {
    return (
      <div className={s.page}>
        <div className={s.empty}>
          Your account isn't linked to a Staff record yet. Ask an admin to
          set <code>linkedEntityType=STAFF</code> and <code>linkedEntityId</code>
          on your user.
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      {/* ── Premium hero ──────────────────────────────────────── */}
      <header className={s.hero}>
        <div className={s.heroIcon} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
            <path d="M6 12v5c3 3 9 3 12 0v-5"/>
          </svg>
        </div>
        <div className={s.heroBody}>
          <div className={s.eyebrow}>Teacher · Gradebook</div>
          <h1 className={s.heroTitle}>Your classes, your grades.</h1>
          <p className={s.heroSub}>
            Every class you teach — encode grades from one place. Your
            changes auto-save locally as you type and notify each student
            the moment you press <strong>Save grades</strong>.
          </p>
        </div>
        {termLabel && (
          <div className={s.heroTerm} title="Active term">{termLabel}</div>
        )}
      </header>

      {/* ── Stat tiles ────────────────────────────────────────── */}
      <div className={s.statRow}>
        <StatTile
          label="Classes"
          value={data.loading ? "…" : stats.classes}
          hint={stats.sections > 0
            ? `across ${stats.sections} section${stats.sections === 1 ? "" : "s"}`
            : (scope === "active" ? "this term" : "across every term")}
        />
        <StatTile
          label="Students"
          value={data.loading ? "…" : stats.uniqueStudents}
          hint={cards.length > 0 ? "graded slots = students × periods" : "—"}
          tone="info"
        />
        <StatTile
          label="Encoded"
          value={data.loading || gradesLoading ? "…" : `${stats.combinedPct}%`}
          hint={stats.periodHint || "—"}
          tone={stats.combinedPct >= 75 ? "good" : stats.combinedPct >= 40 ? "warn" : "danger"}
        />
        <StatTile
          label="Unsaved drafts"
          value={data.loading ? "…" : stats.drafts}
          hint={stats.drafts === 0
            ? "all in sync with server"
            : `${stats.drafts} cell${stats.drafts === 1 ? "" : "s"} edited locally`}
          tone={stats.drafts > 0 ? "warn" : "good"}
        />
      </div>

      {/* ── Filter bar ────────────────────────────────────────── */}
      <div className={s.filterBar}>
        <div className={s.scopeTabs} role="tablist" aria-label="Term scope">
          <button
            type="button"
            role="tab"
            aria-selected={scope === "active"}
            className={`${s.scopeTab} ${scope === "active" ? s.scopeTabActive : ""}`}
            onClick={() => setScope("active")}>
            Active term
            {termLabel && <span className={s.scopeTabSubtle}>{termLabel}</span>}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={scope === "all"}
            className={`${s.scopeTab} ${scope === "all" ? s.scopeTabActive : ""}`}
            onClick={() => setScope("all")}>
            All terms
          </button>
        </div>

        <label className={s.filterField}>
          <span>Section</span>
          <select
            value={sectionId}
            onChange={e => setSectionId(e.target.value)}>
            <option value="">All sections</option>
            {sectionOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className={s.filterField}>
          <span>Sort</span>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}>
            <option value="needs">Needs encoding first</option>
            <option value="subject">By subject</option>
            <option value="section">By section</option>
          </select>
        </label>
      </div>

      {/* ── Loading skeleton ──────────────────────────────────── */}
      {data.loading && (
        <div className={s.skel}>
          <div className={s.skelCard} />
          <div className={s.skelCard} />
          <div className={s.skelCard} />
        </div>
      )}

      {!data.loading && data.error && (
        <div className={s.error}>{data.error}</div>
      )}

      {/* ── Empty state ───────────────────────────────────────── */}
      {!data.loading && !data.error && cards.length === 0 && (
        <div className={s.emptyPremium}>
          <div className={s.emptyPremiumTitle}>
            {scope === "active"
              ? "No classes for the active term yet."
              : "No classes on file."}
          </div>
          <div className={s.emptyPremiumSub}>
            {scope === "active"
              ? <>Switch to <strong>All terms</strong> above, or ask the registrar to add class offerings to your account.</>
              : "Once the registrar assigns class offerings to you, they'll appear here."}
          </div>
        </div>
      )}

      {/* ── Class grid ────────────────────────────────────────── */}
      {!data.loading && !data.error && cards.length > 0 && (
        <div className={s.grid}>
          {cards.map((c, idx) => {
            const subjLabel = c.subject?.code || c.subject?.title || `S${c.offering.subjectId}`;
            const hue       = hueFor(subjLabel);
            const ab        = abbrev(subjLabel);
            const status    = c.draftCount > 0
              ? { tone: "warn", label: `${c.draftCount} unsaved` }
              : c.pendingCells === 0 && c.roster > 0
              ? { tone: "good", label: "All caught up" }
              : c.pendingCells > 0
              ? { tone: "danger", label: `${c.pendingCells} cell${c.pendingCells === 1 ? "" : "s"} to go` }
              : { tone: "info", label: "Empty roster" };
            return (
              <article
                key={c.offering.id}
                className={s.card}
                style={{ "--gHue": hue, animationDelay: `${Math.min(idx, 8) * 45}ms` }}>
                <header className={s.cardHead}>
                  <div className={s.cardIcon} aria-hidden="true">{ab}</div>
                  <div className={s.cardIdent}>
                    <div className={s.cardTitle}
                         title={c.subject?.title || `Subject #${c.offering.subjectId}`}>
                      {c.subject?.title || `Subject #${c.offering.subjectId}`}
                    </div>
                    {c.subject?.code && (
                      <div className={s.cardCode}>{c.subject.code}</div>
                    )}
                  </div>
                  <span className={`${s.statusPill} ${s["statusPill_" + status.tone]}`}>
                    {status.label}
                  </span>
                </header>

                <div className={s.factRow}>
                  {c.section && (
                    <span className={s.factPill}>
                      {c.grade ? `${c.grade.name} · ${c.section.name}` : c.section.name}
                    </span>
                  )}
                  {c.semester && (
                    <span className={s.factPill}>{c.semester.label} sem</span>
                  )}
                  {c.offering.schedule && (
                    <span className={s.factPill}>{c.offering.schedule}</span>
                  )}
                  {c.offering.room && (
                    <span className={s.factPill}>Room {c.offering.room}</span>
                  )}
                </div>

                <div className={s.progressRow}>
                  {c.periods.map(pp => {
                    const pct = c.roster > 0 ? Math.round((pp.count / c.roster) * 100) : 0;
                    return (
                      <div key={pp.code} className={s.progressBlock}>
                        <div className={s.progressLabel}>
                          <span>{pp.label}</span>
                          <span className={s.progressNumeric}>
                            {pp.count} / {c.roster || "—"}
                          </span>
                        </div>
                        <div className={s.progressTrack} aria-hidden="true">
                          <div
                            className={`${s.progressFill} ${pct >= 75 ? s.progressFill_good : pct >= 40 ? s.progressFill_mid : s.progressFill_low}`}
                            style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className={s.cta}
                  onClick={() => navigate(`/teacher/grades/${c.offering.id}`)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.4"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/>
                  </svg>
                  Open encoder
                </button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Small pieces ────────────────────────────────────────────────────

function StatTile({ label, value, hint, tone }) {
  return (
    <div className={`${s.statTile} ${tone ? s["statTile_" + tone] : ""}`}>
      <div className={s.statLabel}>{label}</div>
      <div className={s.statValue}>{value}</div>
      <div className={s.statHint} title={hint}>{hint}</div>
    </div>
  );
}
